import { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { provideHooks } from "../../../../../helpers/routeHooks";
import ProductListingPage from "../../../../../components/ProductListingPage";
import ProductListSkeleton from "../../../../../components/Results/ProductListSkeleton";
import { withTranslation } from "../../../../../components/translation";
import { CHROME_BREADCRUMB_ID } from "../../../../../components/constants";
import { useBreadcrumbUpdates } from "../../../../../components/hooks";
import { useUpdateRefineParameters } from "../../../../../components/hooks/useUpdateRefineParameters";
import {
  fetchSearch,
  getItemCount,
  hasFacets,
  getPaginationData,
  getIsNewSearch,
  getSearchTerm,
  sendPlacementOnLoadBeacon,
} from "../../../../../state/modules/search";
import {
  setPageLoading,
  setIsNewPage,
} from "../../../../../state/modules/page/actions";
import { analyticsPageLoad } from "../../../../../state/modules/analytics/actions";
import { getError } from "../../../../../state/modules/error";
import { getIsLoading } from "../../../../../state/modules/page/selectors";
import {
  clearCategoryDetails,
  clearCuratedCategoryTreatments,
} from "../../../../../state/modules/category/actions/";

const EMPTY_BREADCRUMBS = [];

const isValidSearch = (location) => {
  const searchTerm =
    (new URLSearchParams(location?.search).get("q") || "").trim().length > 0;

  return searchTerm;
};

export const hooks = {
  fetch: ({ dispatch, location, store, uuid, xSiteOrigin, isNewPage }) => {
    const state = store.getState();
    if (isValidSearch(location)) {
      if (!getIsNewSearch(state)) {
        return;
      }

      return dispatch(fetchSearch(uuid, xSiteOrigin, isNewPage, location))
        .then(() => {
          dispatch(sendPlacementOnLoadBeacon());
          if (isNewPage) {
            dispatch(clearCategoryDetails());
            dispatch(clearCuratedCategoryTreatments());
            dispatch(analyticsPageLoad());
            dispatch(setIsNewPage(false));
          }
        })
        .catch((error) => {
          if (typeof window === "undefined") {
            throw error;
          }
        })
        .finally(() => {
          dispatch(setPageLoading(false));
        });
    }

    dispatch(setPageLoading(false));
  },
};

export const SearchPage = ({
  formatTranslation,
  location,
  topContent = null,
}) => {
  const hasFacetsValue = useSelector(hasFacets);
  const itemCount = useSelector(getItemCount);
  const isLoading = useSelector(getIsLoading);
  const paginationData = useSelector(getPaginationData);
  const searchTerm = useSelector(getSearchTerm);
  const error = useSelector(getError);

  const page = paginationData?.page;

  useBreadcrumbUpdates(EMPTY_BREADCRUMBS);

  useUpdateRefineParameters({ searchTerm });

  useEffect(() => {
    if (searchTerm) {
      const { initial, max } = page;

      document.title = formatTranslation("plp_web_search_page_title", {
        searchTerm,
        currentPage: initial,
        maxPage: max,
      });
    }
  }, [searchTerm]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const breadcrumbElement = document.getElementById(CHROME_BREADCRUMB_ID);

    if (!breadcrumbElement) {
      return undefined;
    }

    const previousDisplay = breadcrumbElement.style.display;

    breadcrumbElement.style.display = "none";

    return () => {
      breadcrumbElement.style.display = previousDisplay;
    };
  }, []);

  if (isLoading) {
    return <ProductListSkeleton />;
  }

  return (
    <ProductListingPage
      hasFacets={hasFacetsValue}
      itemCount={itemCount}
      paginationData={paginationData}
      error={error}
      searchTerm={searchTerm}
      location={location}
      topContent={topContent}
    />
  );
};

SearchPage.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  location: PropTypes.object,
  topContent: PropTypes.node,
};

export default provideHooks(hooks)(withTranslation(SearchPage));
